import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import { exportChangedNominationsToWbd } from '../lib/api';

/**
 * Administration → Export changed entries to WBD. Produces the SAME WBD import
 * CSV as the full "Export to WBD" page, but filtered to only entries that have
 * had a change request approved — or been manually edited — after a chosen
 * date/time, so staff can run a small, targeted wp-all-import of just the
 * recent changes instead of re-importing every entry. Super-admin only (gated
 * in the router and nav).
 */

// Format a Date as the `YYYY-MM-DDTHH:mm` string a native datetime-local input
// expects, in the browser's LOCAL time (the input has no timezone concept).
function toLocalInputValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

function sevenDaysAgo(): string {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return toLocalInputValue(d);
}

export function ChangedExport() {
  const [since, setSince] = useState<string>(sevenDaysAgo);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFile, setLastFile] = useState<string | null>(null);

  const handleExport = async () => {
    if (!since) {
      setError('Please choose a date and time first.');
      return;
    }
    setDownloading(true);
    setError(null);
    try {
      // The datetime-local value is local time; convert to a UTC ISO instant.
      const sinceIso = new Date(since).toISOString();
      const filename = await exportChangedNominationsToWbd(sinceIso);
      setLastFile(filename);
    } catch (err) {
      console.error(err);
      setError('The export failed. Please try again, or contact support if it keeps happening.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Stack spacing={3} alignItems="center">
      <Typography variant="h3" component="h1" align="center">
        Export changed entries to WBD
      </Typography>

      <Paper sx={{ p: { xs: 3, sm: 4 }, maxWidth: 720, width: '100%' }}>
        <Stack spacing={3} alignItems="center" textAlign="center">
          <Box
            sx={(t) => ({
              width: 64,
              height: 64,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: t.palette.mode === 'dark' ? 'rgba(255,204,0,0.16)' : 'rgba(255,204,0,0.22)',
            })}
          >
            <HistoryOutlinedIcon fontSize="large" sx={{ color: 'text.primary' }} />
          </Box>

          <Stack spacing={1}>
            <Typography variant="h6">Download a targeted WBD import file</Typography>
            <Typography variant="body2" color="text.secondary">
              This downloads a .csv in the same column format as the full "Export to WBD" file, but
              containing only the entries whose change requests were approved — or that were manually
              edited — after the time you choose. Feed it straight into wp-all-import to sync just the
              recent edits instead of re-importing everything.
            </Typography>
          </Stack>

          <TextField
            type="datetime-local"
            label="Approved after"
            value={since}
            onChange={(e) => setSince(e.target.value)}
            helperText="Only export entries with a change request approved, or a manual edit, after this time."
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ width: '100%', maxWidth: 320 }}
          />

          <Alert severity="info" sx={{ width: '100%', textAlign: 'left' }}>
            Image links point at the live portal, so run the WordPress import while these entries
            and their images remain available on the admin portal.
          </Alert>

          <Button
            variant="contained"
            size="large"
            onClick={handleExport}
            disabled={downloading || !since}
            startIcon={
              downloading ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <FileDownloadOutlinedIcon />
              )
            }
          >
            {downloading ? 'Preparing…' : 'Export changed entries'}
          </Button>

          {lastFile && !error && (
            <Alert severity="success" sx={{ width: '100%' }}>
              Downloaded <strong>{lastFile}</strong>. Check your browser's downloads.
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ width: '100%' }}>
              {error}
            </Alert>
          )}
        </Stack>
      </Paper>
    </Stack>
  );
}
